import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'src/store/use-dispatch';
import { fetchUsers } from 'shared/store/actions/users-management.actions';
import scheduleServiceApiRaw from 'shared/api/schedule.service.api';
import { SCHEDULE_EVENT_TYPES } from 'shared/constants/schedule.constants';
import { DEFAULT_TIMEZONE } from 'shared/utils/date.utils';
import Logger from 'shared/utils/Logger';
import { loadWorkbookGrids } from './helpers/workbook.loader';
import {
  buildImportPayload,
  buildImportPlan,
  buildImportResult,
  toOverrideWindow,
} from './helpers/analysis';
import type { ImportPlan } from './helpers/analysis';
import { WIZARD_STEPS } from './helpers/upload.types';
import type {
  ImportResult,
  ImportScheduleResponse,
  LastUpload,
  WizardStep,
} from './helpers/upload.types';
import type { SheetGrid } from './helpers/workbook.types';
import {
  autoSelectTabs,
  isAcceptedFile,
  orderTabsForDisplay,
} from './helpers/upload.utils';

type ChosenFile = {
  name: string;
  size: number;
};

type OrgUser = {
  email?: string;
};

export const FILE_ERRORS = {
  UNSUPPORTED_TYPE: 'UNSUPPORTED_TYPE',
  UNREADABLE: 'UNREADABLE',
} as const;

export type FileError = (typeof FILE_ERRORS)[keyof typeof FILE_ERRORS];

// schedule.service.api.js is untyped, so TS narrows its params to the defaults.
// Same workaround as shared/hooks/schedule.hooks.ts.
const scheduleServiceApi = scheduleServiceApiRaw as unknown as {
  getScheduleEvents: (params: Record<string, unknown>) => Promise<{ total?: number }>;
  importSchedule: (payload: Record<string, unknown>) => Promise<ImportScheduleResponse>;
};

// The API is a single request with no progress events, so the bar creeps to a
// ceiling while it is in flight and only completes when the response lands.
const PROGRESS_TICK_MS = 160;
const PROGRESS_STEP = 7;
const PROGRESS_CEILING = 90;
const PROGRESS_COMPLETE = 100;

const COUNT_ONLY_PAGE_SIZE = 1;

const logger = Logger.create({ loggerName: 'ShiftsUpload' });

// Scoped to ASSIGNMENT: the import only replaces shifts, so counting educational
// events here would tell the admin more is being removed than actually is.
const countExistingAssignmentsInRange = async (plan: ImportPlan, orgTimezone: string) => {
  if (!plan.startDate || !plan.endDate) return 0;

  const { total } = await scheduleServiceApi.getScheduleEvents({
    ...toOverrideWindow(plan.startDate, plan.endDate, orgTimezone),
    eventType: [SCHEDULE_EVENT_TYPES.ASSIGNMENT],
    pageSize: COUNT_ONLY_PAGE_SIZE,
  });

  return total ?? 0;
};

export const useShiftsUpload = ({
  onUploaded,
  onUploadFailed,
}: {
  onUploaded: (result: ImportResult) => void;
  onUploadFailed: () => void;
}) => {
  const dispatch = useDispatch();
  const users = useSelector(
    ({ usersManagement }: { usersManagement: { list: OrgUser[] | null } }) => usersManagement.list,
  );
  const orgTimezone = useSelector(
    ({ organization }: { organization?: { timezone?: string } }) => organization?.timezone,
  );

  const [step, setStep] = useState<WizardStep>(WIZARD_STEPS.CHOOSE_FILE);
  const [file, setFile] = useState<ChosenFile | null>(null);
  const [grids, setGrids] = useState<SheetGrid[]>([]);
  const [selectedTabs, setSelectedTabs] = useState<string[]>([]);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [removedCount, setRemovedCount] = useState<number | null>(null);
  const [results, setResults] = useState<ImportResult[]>([]);
  const [lastUpload, setLastUpload] = useState<LastUpload | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileError, setFileError] = useState<FileError | null>(null);
  const [isReadingFile, setIsReadingFile] = useState(false);

  // Only the very first visitor of the session pays for this; every other
  // screen that needs the roster primes the same slice.
  useEffect(() => {
    if (users === null) dispatch(fetchUsers());
  }, [users, dispatch]);

  const timezone = orgTimezone || DEFAULT_TIMEZONE;

  const knownEmails = useMemo(
    () =>
      new Set((users ?? []).map(user => (user.email ?? '').trim().toLowerCase()).filter(Boolean)),
    [users],
  );

  const sheetNames = useMemo(() => orderTabsForDisplay(grids.map(grid => grid.sheetName)), [grids]);

  // Analysis needs the roster to resolve attendees, so the Analyzing step covers
  // the wait instead of every tab coming back "nothing to import" because the
  // roster had not landed yet.
  useEffect(() => {
    if (step !== WIZARD_STEPS.ANALYZING || users === null) return;

    let cancelled = false;

    const analyse = async () => {
      const nextPlan = buildImportPlan({
        grids,
        selectedTabs,
        orgTimezone: timezone,
        knownEmails,
      });

      let existingAssignments: number | null = null;
      try {
        existingAssignments = await countExistingAssignmentsInRange(nextPlan, timezone);
      } catch (error) {
        logger.error('Could not count the assignments an import would replace', {
          error,
          startDate: nextPlan.startDate,
          endDate: nextPlan.endDate,
        });
        existingAssignments = null;
      }

      if (cancelled) return;

      setPlan(nextPlan);
      setRemovedCount(existingAssignments);
      setStep(WIZARD_STEPS.REVIEW);
    };

    analyse();

    return () => {
      cancelled = true;
    };
  }, [step, grids, selectedTabs, users, knownEmails, timezone]);

  const resetToChooseFile = useCallback(() => {
    setFile(null);
    setGrids([]);
    setSelectedTabs([]);
    setPlan(null);
    setRemovedCount(null);
    setUploadProgress(0);
    setStep(WIZARD_STEPS.CHOOSE_FILE);
  }, []);

  const chooseFile = useCallback(
    async (rawFile: File) => {
      if (!isAcceptedFile(rawFile.name)) {
        resetToChooseFile();
        setFileError(FILE_ERRORS.UNSUPPORTED_TYPE);
        return;
      }

      setFileError(null);
      setIsReadingFile(true);

      try {
        const loaded = await loadWorkbookGrids(rawFile);

        setGrids(loaded);
        setFile({ name: rawFile.name, size: rawFile.size });
        setSelectedTabs(autoSelectTabs(loaded.map(grid => grid.sheetName)));
        setStep(WIZARD_STEPS.SELECT_TABS);
      } catch (error) {
        logger.error('Could not read the shifts workbook', {
          error,
          fileName: rawFile.name,
          fileSize: rawFile.size,
        });
        resetToChooseFile();
        setFileError(FILE_ERRORS.UNREADABLE);
      } finally {
        setIsReadingFile(false);
      }
    },
    [resetToChooseFile],
  );

  const toggleTab = useCallback((sheetName: string) => {
    setSelectedTabs(current =>
      current.includes(sheetName)
        ? current.filter(name => name !== sheetName)
        : [...current, sheetName],
    );
  }, []);

  const backToChooseFile = useCallback(() => {
    resetToChooseFile();
    setFileError(null);
  }, [resetToChooseFile]);

  const confirmUpload = useCallback(async () => {
    if (!plan || !file) return;

    setStep(WIZARD_STEPS.UPLOADING);
    setUploadProgress(0);

    const timer = setInterval(
      () => setUploadProgress(current => Math.min(current + PROGRESS_STEP, PROGRESS_CEILING)),
      PROGRESS_TICK_MS,
    );

    try {
      const response = await scheduleServiceApi.importSchedule(
        buildImportPayload({ plan, fileName: file.name, sheets: selectedTabs }),
      );

      setUploadProgress(PROGRESS_COMPLETE);

      const result = buildImportResult({
        response,
        plan,
        fileName: file.name,
        completedAt: Date.now(),
      });

      if (response.scheduleEventsCreated !== plan.events.length) {
        logger.warn('Server stored fewer shifts than were sent', {
          fileName: file.name,
          sent: plan.events.length,
          created: response.scheduleEventsCreated,
          skippedByServer: response.scheduleEventsSkipped,
          attendeesUnresolved: response.attendeesUnresolved,
          groupsCreated: response.scheduleEventGroupsCreated,
          groupsReused: response.scheduleEventGroupsReused,
        });
      }

      setResults(current => [result, ...current]);
      setLastUpload({ fileName: result.fileName, uploadedAt: result.completedAt });
      onUploaded(result);
      backToChooseFile();
    } catch (error) {
      logger.error('Shift import failed', {
        error,
        fileName: file.name,
        events: plan.events.length,
        groups: plan.groups.length,
        startDate: plan.startDate,
        endDate: plan.endDate,
      });
      setUploadProgress(0);
      setStep(WIZARD_STEPS.REVIEW);
      onUploadFailed();
    } finally {
      clearInterval(timer);
    }
  }, [plan, file, selectedTabs, backToChooseFile, onUploaded, onUploadFailed]);

  return {
    step,
    file,
    sheetNames,
    selectedTabs,
    plan,
    removedCount,
    results,
    lastUpload,
    uploadProgress,
    fileError,
    isReadingFile,
    chooseFile,
    confirmUpload,
    dismissResult: useCallback(
      (id: string) => setResults(current => current.filter(result => result.id !== id)),
      [],
    ),
    toggleTab,
    selectAllTabs: useCallback(() => setSelectedTabs(sheetNames), [sheetNames]),
    clearTabs: useCallback(() => setSelectedTabs([]), []),
    backToChooseFile,
    backToSelectTabs: useCallback(() => setStep(WIZARD_STEPS.SELECT_TABS), []),
    requestAnalysis: useCallback(() => setStep(WIZARD_STEPS.ANALYZING), []),
  };
};
